import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Bars3Icon, XMarkIcon, EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import UserMenu from '../../common/components/Navigation/UserMenu';
import { Button, Sheet, SheetBody, SheetHeader } from '../../common/components/ui';

// How many destinations the mobile tab bar shows before the rest move behind
// "More". Five is the ceiling on a 375px screen: below ~64px per tab the
// labels start truncating and the targets stop being comfortably thumb-sized.
const MOBILE_TAB_SLOTS = 4;

/**
 * The application shell.
 *
 * Two distinct navigations, not one navigation that shrinks:
 *   • >=md — a persistent left sidebar, collapsible to an icon rail
 *   • <md  — a bottom tab bar for the primary destinations plus a "More"
 *            sheet for the overflow
 *
 * A sidebar that merely narrows on a phone is the usual desktop-first
 * compromise and it is a bad one: it puts navigation at the top-left, which is
 * the hardest place on a handset for a thumb to reach. The bottom bar is where
 * a native app puts it, so that is where this puts it.
 *
 * The shell itself never scrolls — see the app-shell contract in styles.css.
 * Pages provide their own scroll container via the <Page> primitive.
 *
 * `overlay` is a slot for something that hovers over the content on every
 * route — rendered inside `<main>`, so it is positioned against the CONTENT
 * area and cannot land on the tab bar below. Taken as a prop rather than
 * imported, for the reason `DebugPanel` takes its `tabs`: this layer has to
 * stay reachable from a future mobile or desktop target, which means it
 * cannot know what the app chose to float there.
 */
const DashboardLayout = ({
  children,
  navigation,
  title = 'Dashboard',
  brandName = 'OddSignals',
  logo,
  searchBar,
  userMenuOptions,
  immersiveRoutes = [],
  overlay = null,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [railed, setRailed] = useState(false);
  const permissions = useSelector((state) => state.auth.permissions || []);
  const navigate = useNavigate();
  const location = useLocation();

  // Some screens ARE the content — a canvas editor, a video timeline. On a
  // phone the app's own tab bar costs them ~90px and competes with their own
  // controls, so they get the whole screen and navigation moves into the
  // header's drawer. The caller decides which routes; the layout stays
  // app-agnostic.
  const immersive = immersiveRoutes.some(
    (prefix) => location.pathname === prefix || location.pathname.startsWith(`${prefix}/`)
  );

  const filterNavigation = useCallback(
    (items) =>
      items
        .map((item) => ({
          ...item,
          children: item.children ? filterNavigation(item.children) : undefined,
        }))
        .filter(
          (item) =>
            !item.permissions ||
            item.permissions.every((p) => permissions.includes(p))
        ),
    [permissions]
  );

  const filteredNavigation = useMemo(
    () => filterNavigation(navigation),
    [filterNavigation, navigation]
  );

  // Flatten the grouped tree into the ordered list of leaf destinations. The
  // tab bar and the "More" sheet are both built from this, so a nav item added
  // to the config in App.jsx shows up on mobile without further work.
  const leaves = useMemo(() => {
    const walk = (items) =>
      items.reduce((acc, item) => {
        if (item.children?.length) return acc.concat(walk(item.children));
        if (item.href) acc.push(item);
        return acc;
      }, []);
    return walk(filteredNavigation);
  }, [filteredNavigation]);

  const tabs = leaves.slice(0, MOBILE_TAB_SLOTS);
  const overflow = leaves.slice(MOBILE_TAB_SLOTS);

  // Collect every nav href so we can tell when one href is a prefix of another
  // (e.g. '/media' vs '/media/editor'). A link whose href prefixes a sibling
  // must match EXACTLY (NavLink `end`), otherwise React Router's default
  // startsWith behaviour lights up the parent while you are on the child.
  const allHrefs = useMemo(() => leaves.map((i) => i.href), [leaves]);
  const needsExactMatch = useCallback(
    (href) => !!href && allHrefs.some((o) => o !== href && o.startsWith(`${href}/`)),
    [allHrefs]
  );

  // Close any overlay on navigation, so a tap in the drawer doesn't leave it
  // hanging open over the page it just navigated to.
  useEffect(() => {
    setDrawerOpen(false);
    setMoreOpen(false);
  }, [location.pathname]);

  // Page-scroll locking and Escape-to-close now live in <Sheet>, so both
  // overlays get them from one place.

  // `reloadOnSamePage` items should snap back to their landing state when the
  // user taps the nav entry for the route they are already on. NavLink's
  // default is a no-op for a same-path click, so query/hash survive and the
  // page keeps showing its filtered table. Force a navigation with the bare
  // href and a fresh `resetAt` so the page's reset effect can tell a "go back
  // to landing" click from any other render.
  const handleNavClick = (e, item) => {
    if (!item.reloadOnSamePage || !item.href) return;
    const target = item.href.split('?')[0].split('#')[0];
    if (location.pathname !== target) return;
    e.preventDefault();
    navigate(item.href, { replace: true, state: { resetAt: Date.now() } });
  };

  const sidebarLinkClass = ({ isActive }) =>
    [
      'group flex items-center gap-3 rounded-pill px-3 py-2.5 text-body-sm font-medium',
      'transition-colors duration-150',
      railed ? 'justify-center px-0' : '',
      isActive
        ? 'bg-accent text-accent-ink'
        : 'text-ink-muted hover:bg-surface-raised hover:text-ink',
    ].join(' ');

  const renderSidebarItems = (items, depth = 0) =>
    items.map((item, i) => {
      if (item.children?.length) {
        return (
          <div key={`${item.name}-${i}`} className="mb-5">
            {!railed && (
              <div className="mb-2 px-3 text-eyebrow uppercase text-ink-subtle">{item.name}</div>
            )}
            <div className="space-y-1">{renderSidebarItems(item.children, depth + 1)}</div>
          </div>
        );
      }
      const Icon = item.icon;
      return (
        <NavLink
          key={item.href || `${item.name}-${i}`}
          to={item.href}
          end={needsExactMatch(item.href)}
          title={item.name}
          onClick={(e) => handleNavClick(e, item)}
          className={sidebarLinkClass}
        >
          {Icon && <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />}
          {!railed && <span className="truncate">{item.name}</span>}
        </NavLink>
      );
    });

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-canvas">
      {/* Fills the status-bar strip on a notched device so the app reads as
          full-bleed rather than letterboxed. */}
      <div style={{ height: 'var(--safe-top)' }} className="shrink-0 bg-canvas" />

      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* ── Desktop sidebar ─────────────────────────────────────────────
            Hidden entirely below md — the bottom bar takes over there, so
            this never has to double as a phone navigation. */}
        <aside
          className={[
            'hidden shrink-0 flex-col border-r border-line bg-surface md:flex',
            'transition-[width] duration-200 ease-out',
            railed ? 'w-[72px]' : 'w-[248px]',
          ].join(' ')}
        >
          <div className="flex h-16 shrink-0 items-center justify-between gap-2 px-4">
            {!railed && (
              <NavLink to="/" className="flex min-w-0 items-center truncate text-body font-medium tracking-tight text-ink">
                {logo ? <img src={logo} alt={brandName} className="h-7 w-auto" /> : brandName}
              </NavLink>
            )}
            <Button
              variant="ghost"
              icon
              onClick={() => setRailed((v) => !v)}
              aria-label={railed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="shrink-0"
            >
              <svg
                className={`h-5 w-5 transition-transform duration-200 ${railed ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </Button>
          </div>

          <nav className="flex-1 overflow-y-auto overscroll-contain px-3 py-4">{renderSidebarItems(filteredNavigation)}</nav>

          <div
            className={`shrink-0 border-t border-line p-3 ${railed ? 'flex justify-center' : ''}`}
          >
            <UserMenu {...userMenuOptions} />
          </div>
        </aside>

        {/* ── Main column ─────────────────────────────────────────────────── */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <header className={`z-40 flex shrink-0 items-center gap-3 border-b border-line bg-canvas px-4 md:h-16 ${immersive ? 'h-12' : 'h-14'}`}>
            {/* The drawer button exists only on mobile, and only when there is
                grouped navigation worth opening. */}
            <Button
              variant="ghost"
              icon
              onClick={() => setDrawerOpen(true)}
              aria-label="Open navigation"
              className="-ml-2 md:hidden"
            >
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </Button>

            <NavLink
              to="/"
              className="flex items-center truncate text-body font-medium tracking-tight text-ink md:hidden"
              style={{ minHeight: 'var(--tap-target)' }}
            >
              {brandName}
            </NavLink>

            <div className="min-w-0 flex-1">{searchBar ?? null}</div>

            <span className="hidden truncate text-body-sm text-ink-muted md:inline">{title}</span>
          </header>

          {/* `relative` is what makes `overlay` positionable against the
              CONTENT area rather than the viewport. A floating control fixed
              to the viewport's bottom edge lands on top of the phone tab bar
              below — and on its "More" button, on the right — so anything
              that wants to hover over the app anchors here instead, and needs
              no number for a bar whose height it would get wrong. */}
          <main className="relative min-h-0 flex-1 overflow-hidden" data-main-scroll>
            {children}
            {overlay}
          </main>

          {/* ── Mobile tab bar ───────────────────────────────────────────────
              Sits below the content and above the home indicator. The
              safe-area padding is on the bar itself so the tappable row never
              ends up under the indicator. */}
          <nav
            className={`z-40 shrink-0 border-t border-line bg-surface md:hidden ${immersive ? 'hidden' : ''}`}
            style={{ paddingBottom: 'var(--safe-bottom)' }}
            aria-label="Primary"
          >
            <div className="flex items-stretch">
              {tabs.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    end={needsExactMatch(item.href)}
                    onClick={(e) => handleNavClick(e, item)}
                    className={({ isActive }) =>
                      [
                        'flex min-w-0 flex-1 flex-col items-center justify-center gap-1',
                        'px-1 py-2 text-caption-sm font-medium transition-colors',
                        isActive ? 'text-ink' : 'text-ink-subtle',
                      ].join(' ')
                    }
                    style={{ minHeight: 'var(--tap-target)' }}
                  >
                    {Icon && <Icon className="h-6 w-6 shrink-0" aria-hidden="true" />}
                    <span className="w-full truncate text-center leading-none">{item.name}</span>
                  </NavLink>
                );
              })}

              {overflow.length > 0 && (
                <button
                  type="button"
                  onClick={() => setMoreOpen(true)}
                  aria-label="More destinations"
                  className="flex min-w-0 flex-1 flex-col items-center justify-center gap-1 px-1 py-2 text-caption-sm font-medium text-ink-subtle"
                  style={{ minHeight: 'var(--tap-target)' }}
                >
                  <EllipsisHorizontalIcon className="h-6 w-6 shrink-0" aria-hidden="true" />
                  <span className="leading-none">More</span>
                </button>
              )}
            </div>
          </nav>
        </div>
      </div>

      {/* ── Mobile drawer: the full grouped navigation ────────────────────── */}
      {drawerOpen && (
        <Sheet
          open
          onClose={() => setDrawerOpen(false)}
          label="Navigation"
          className="md:hidden"
        >
          <SheetHeader>
            <span className="text-body font-medium text-ink">{brandName}</span>
            <Button
              variant="ghost"
              icon
              onClick={() => setDrawerOpen(false)}
              aria-label="Close navigation"
              className="-mr-2"
            >
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </Button>
          </SheetHeader>
          <SheetBody>{renderSidebarItems(filteredNavigation)}</SheetBody>
          <div className="shrink-0 border-t border-line px-5 py-4">
            <UserMenu {...userMenuOptions} />
          </div>
        </Sheet>
      )}

      {/* ── Mobile "More" sheet: the tab-bar overflow ─────────────────────── */}
      {moreOpen && (
        <Sheet
          open
          onClose={() => setMoreOpen(false)}
          label="More destinations"
          className="md:hidden"
        >
          <SheetHeader>
            <span className="text-body font-medium text-ink">More</span>
            <Button
              variant="ghost"
              icon
              onClick={() => setMoreOpen(false)}
              aria-label="Close"
              className="-mr-2"
            >
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </Button>
          </SheetHeader>
          <SheetBody className="space-y-1">
              {overflow.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    end={needsExactMatch(item.href)}
                    onClick={(e) => handleNavClick(e, item)}
                    className={({ isActive }) =>
                      [
                        'flex items-center gap-3 rounded-field px-3 text-body-sm font-medium',
                        isActive ? 'bg-accent text-accent-ink' : 'text-ink-body',
                      ].join(' ')
                    }
                    style={{ minHeight: 'var(--tap-target)' }}
                  >
                    {Icon && <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />}
                    <span className="truncate">{item.name}</span>
                  </NavLink>
                );
            })}
          </SheetBody>
        </Sheet>
      )}
    </div>
  );
};

export default DashboardLayout;
